import React from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useUpdateInvoice } from 'api/invoices/use-update-invoice';
import type { UpdateInvoiceReq } from 'api/swagger';
import { UserRole, withRoleGuard } from 'shared/hocs/with-role-guard';
import { formatDate } from 'shared/utils/time/format-date';
import { FormDrawerWithButton, EFormDrawerWithButtonIconType } from 'components/common/form-drawer-with-button';
import type { IInvoiceEditProps } from 'components/invoices/invoice-edit/types';
import { InvoiceEditForm } from 'components/invoices/invoice-edit-form';
import type { IInvoiceEditFormFields } from 'components/invoices/invoice-edit-form';

export const InvoiceEditComponent: React.FC<IInvoiceEditProps> = (props) => {
  const { invoice } = props;

  const { t } = useTranslation();
  const { mutateAsync: updateInvoice, isLoading } = useUpdateInvoice(invoice.id);

  const onFinish = async (values: IInvoiceEditFormFields) => {
    const apiVariables: UpdateInvoiceReq = {
      currency: values.currency,
      price: values.price,
      vatPercent: values.vatPercent,
      startPaymentPeriod: values.startPaymentPeriod && formatDate(values.startPaymentPeriod),
      endPaymentPeriod: values.endPaymentPeriod && formatDate(values.endPaymentPeriod),
      serviceDate: values.serviceDate && formatDate(values.serviceDate),
      issuedDate: values.issuedDate && formatDate(values.issuedDate),
    };

    await updateInvoice(apiVariables);
  };

  const initialValues: IInvoiceEditFormFields = {
    currency: invoice.currency,
    price: invoice.price,
    vatPercent: invoice.vatPercent,
    startPaymentPeriod: invoice.startPaymentPeriod ? dayjs(invoice.startPaymentPeriod) : undefined,
    endPaymentPeriod: invoice.endPaymentPeriod ? dayjs(invoice.endPaymentPeriod) : undefined,
    serviceDate: invoice.serviceDate ? dayjs(invoice.serviceDate) : dayjs(invoice.createdAt),
    issuedDate: invoice.issuedDate ? dayjs(invoice.issuedDate) : undefined,
  };

  return (
    <FormDrawerWithButton
      formComponent={InvoiceEditForm}
      formProps={{ initialValues, status: invoice.status }}
      title={t('invoice:edit_invoice')}
      buttonLabel={t('common:edit')}
      isLoading={isLoading}
      iconType={EFormDrawerWithButtonIconType.EDIT}
      onFinish={onFinish}
    />
  );
};

export const InvoiceEdit = withRoleGuard<IInvoiceEditProps>(InvoiceEditComponent, [
  UserRole.admin,
  UserRole.assistant,
  UserRole.accountant,
]);
